/**
 * Account-level settings page.
 *
 * MVP scope: show which countries XyloTrust supports for SMS verification
 * out-of-the-box. Customers see the list + can request additional countries
 * by emailing support. Expandable later for other account-level prefs.
 */
import React from 'react';
import { Mail } from 'lucide-react';
import { useLang } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { cn } from '../lib/utils';

interface Country {
  code:    string;
  flag:    string;
  dial:    string;
  nameNl:  string;
  nameEn:  string;
  default?: boolean;
}

// 19 countries currently active in the XyloTrust SMS-verification gateway.
// Belgium is flagged as the picker default. Adding a country here is purely
// cosmetic — the actual gateway allow-list lives in the XyloTrust backend.
const COUNTRIES: Country[] = [
  { code: 'BE', flag: '🇧🇪', dial: '+32',  nameNl: 'België',             nameEn: 'Belgium',        default: true },
  { code: 'AT', flag: '🇦🇹', dial: '+43',  nameNl: 'Oostenrijk',         nameEn: 'Austria' },
  { code: 'CZ', flag: '🇨🇿', dial: '+420', nameNl: 'Tsjechië',           nameEn: 'Czechia' },
  { code: 'DK', flag: '🇩🇰', dial: '+45',  nameNl: 'Denemarken',         nameEn: 'Denmark' },
  { code: 'DE', flag: '🇩🇪', dial: '+49',  nameNl: 'Duitsland',          nameEn: 'Germany' },
  { code: 'FR', flag: '🇫🇷', dial: '+33',  nameNl: 'Frankrijk',          nameEn: 'France' },
  { code: 'IE', flag: '🇮🇪', dial: '+353', nameNl: 'Ierland',            nameEn: 'Ireland' },
  { code: 'IT', flag: '🇮🇹', dial: '+39',  nameNl: 'Italië',             nameEn: 'Italy' },
  { code: 'LU', flag: '🇱🇺', dial: '+352', nameNl: 'Luxemburg',          nameEn: 'Luxembourg' },
  { code: 'NL', flag: '🇳🇱', dial: '+31',  nameNl: 'Nederland',          nameEn: 'Netherlands' },
  { code: 'NO', flag: '🇳🇴', dial: '+47',  nameNl: 'Noorwegen',          nameEn: 'Norway' },
  { code: 'PL', flag: '🇵🇱', dial: '+48',  nameNl: 'Polen',              nameEn: 'Poland' },
  { code: 'PT', flag: '🇵🇹', dial: '+351', nameNl: 'Portugal',           nameEn: 'Portugal' },
  { code: 'ES', flag: '🇪🇸', dial: '+34',  nameNl: 'Spanje',             nameEn: 'Spain' },
  { code: 'SE', flag: '🇸🇪', dial: '+46',  nameNl: 'Zweden',             nameEn: 'Sweden' },
  { code: 'CH', flag: '🇨🇭', dial: '+41',  nameNl: 'Zwitserland',        nameEn: 'Switzerland' },
  { code: 'TR', flag: '🇹🇷', dial: '+90',  nameNl: 'Turkije',            nameEn: 'Turkey' },
  { code: 'GB', flag: '🇬🇧', dial: '+44',  nameNl: 'Verenigd Koninkrijk', nameEn: 'United Kingdom' },
  { code: 'US', flag: '🇺🇸', dial: '+1',   nameNl: 'Verenigde Staten',   nameEn: 'United States' },
];

const SUPPORT_EMAIL = 'support@xylosnap.com';

export function Settings() {
  const { t, lang } = useLang();
  const tr = t.settings;
  const trc = tr.smsCountries;

  // Render the intro string while turning **N landen** into bold.
  const introNodes = trc.intro.split(/\*\*([^*]+)\*\*/g).map((seg, i) =>
    i % 2 === 1 ? <strong key={i} className="font-semibold text-gray-900">{seg}</strong> : seg,
  );

  return (
    <div className="max-w-5xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{tr.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{tr.subtitle}</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{trc.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-gray-600 leading-relaxed">{introNodes}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {COUNTRIES.map(c => (
              <div
                key={c.code}
                className={cn(
                  'flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors',
                  c.default
                    ? 'border-brand-500 ring-2 ring-brand-500/20 bg-brand-50/40'
                    : 'border-gray-200 bg-white hover:bg-gray-50',
                )}
              >
                <span className="text-xl leading-none" aria-hidden>{c.flag}</span>
                <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                  {lang === 'nl' ? c.nameNl : c.nameEn}
                </span>
                <span
                  className={cn(
                    'text-xs font-mono font-semibold rounded px-1.5 py-0.5',
                    c.default ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700',
                  )}
                >
                  {c.dial}
                </span>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t border-gray-100 flex items-start gap-3">
            <Mail className="h-4 w-4 text-brand-600 mt-0.5 flex-none" />
            <p className="text-sm text-gray-600 leading-relaxed">
              <strong className="font-semibold text-gray-900">{trc.supportNote}</strong>{' '}
              {trc.supportNoteRest}{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-brand-600 hover:underline font-medium">
                {SUPPORT_EMAIL}
              </a>{' '}
              {trc.supportNoteEnd}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
